import type { PlayerAction, PublicGameState, RoomMember, Trade } from "@grundbesitz/shared";
import { acceptError, DEMAND_STEPS, demandAction, mortgagedNotes } from "../../../lib/assets";
import { formatMoney } from "../../../lib/format";
import { Button } from "../../ui/Button";
import { PlayerChip } from "../assets/PlayerChip";
import { SideView } from "./SideView";

/**
 * One open offer. `incoming`: addressed to the viewer (accept, reject, counter, demand more cash).
 * Otherwise it is the viewer's own offer (withdraw).
 */
export function TradeOfferCard({
  game,
  members,
  trade,
  meId,
  incoming,
  busy,
  onAccept,
  onReject,
  onCounter,
  onDemand,
  onCancel,
}: {
  game: PublicGameState;
  members: RoomMember[];
  trade: Trade;
  meId: string;
  incoming: boolean;
  busy: string | null;
  onAccept: (t: Trade) => void;
  onReject: (t: Trade) => void;
  onCounter: (t: Trade) => void;
  onDemand: (t: Trade, extra: number, action: PlayerAction) => void;
  onCancel: (t: Trade) => void;
}) {
  const other = incoming ? trade.from : trade.to;
  // From the viewer's side: what I hand over, and what I get.
  const mine = incoming ? trade.get : trade.give;
  const theirs = incoming ? trade.give : trade.get;
  const blocked = incoming ? acceptError(game, meId, trade.id) : null;
  const notes = mortgagedNotes(game, theirs);
  const hintId = `trade-${trade.id}-hint`;
  const isCounter = (trade.round ?? 0) > 0;
  const demands = incoming ? DEMAND_STEPS.map((extra) => ({ extra, action: demandAction(game, meId, trade, extra) })) : [];

  return (
    <li className="rounded-xl border border-line bg-surface p-3">
      <p className="flex flex-wrap items-center gap-x-1.5 text-sm">
        {incoming ? (
          <>
            <PlayerChip game={game} members={members} id={other} />
            <span className="text-ink-2">{isCounter ? "macht dir ein Gegenangebot" : "bietet dir an"}</span>
          </>
        ) : (
          <>
            <span className="text-ink-2">{isCounter ? "Dein Gegenangebot an" : "Dein Angebot an"}</span>
            <PlayerChip game={game} members={members} id={other} />
          </>
        )}
      </p>

      <div className="mt-2.5 grid gap-2.5 min-[380px]:grid-cols-2">
        <div>
          <h4 className="mb-1 text-xs font-semibold text-ink-2">Du bekommst</h4>
          <SideView game={game} side={theirs} />
        </div>
        <div>
          <h4 className="mb-1 text-xs font-semibold text-ink-2">Du gibst</h4>
          <SideView game={game} side={mine} />
        </div>
      </div>

      {notes.length > 0 && (
        <div className="mt-2.5 space-y-1 text-xs">
          {notes.map((n) => (
            <p key={n}>
              <span className="font-semibold text-warn">Belastetes Grundstück.</span> {n}
            </p>
          ))}
        </div>
      )}

      {incoming ? (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="primary"
              className="flex-1 basis-28"
              disabled={blocked !== null || busy !== null}
              aria-describedby={blocked ? hintId : undefined}
              onClick={() => onAccept(trade)}
            >
              Annehmen
            </Button>
            <Button className="flex-1 basis-28" disabled={busy !== null} onClick={() => onCounter(trade)}>
              Gegenangebot
            </Button>
            <Button variant="ghost" className="flex-1 basis-28" disabled={busy !== null} onClick={() => onReject(trade)}>
              Ablehnen
            </Button>
          </div>
          {blocked && (
            <p id={hintId} className="mt-2 text-xs font-semibold text-warn">
              Annehmen geht gerade nicht: {blocked}
            </p>
          )}
          <div className="mt-3 border-t border-line pt-2.5">
            <p className="text-xs font-semibold text-ink-2">Nachfordern: gleiches Angebot zurück, mit mehr Geld für dich</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {demands.map(({ extra, action }) => (
                <Button
                  key={extra}
                  variant="secondary"
                  className="min-w-16 flex-1 px-2"
                  disabled={action === null || busy !== null}
                  aria-label={`${formatMoney(extra)} mehr fordern`}
                  onClick={() => action && onDemand(trade, extra, action)}
                >
                  +{extra}
                </Button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="mt-3">
          <Button className="w-full" disabled={busy !== null} onClick={() => onCancel(trade)}>
            Zurückziehen
          </Button>
        </div>
      )}
    </li>
  );
}
